import { Box } from '@material-ui/core';
import { LenderName } from '@nemuru-eng/js-api-client';
import { useFormContext } from 'react-hook-form';

import {
  EditAutoPaymentMethodSummaryRowData,
  EditAutoPaymentMethodSummarySection,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/EditAutoPaymentMethodSummarySection';
import { EditAutoPaymentMethodPreviewConditionsData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/editAutoPaymentMethodPreviewConditions.map';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Font } from '@/components/shared/components/Font';
import { LenderIcon } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { LenderLogo } from '@/components/shared/components/Logo/LenderLogo/LenderLogo';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

interface EditAutoPaymentMethodConditionsSummaryProps {
  initialConditions: EditAutoPaymentMethodPreviewConditionsData;
  newConditions: EditAutoPaymentMethodPreviewConditionsData;
  selectedLenderName?: LenderName;
  isInvalid?: boolean;
}

export const EditAutoPaymentMethodConditionsSummary = ({
  initialConditions,
  newConditions,
  selectedLenderName,
  isInvalid = false,
}: EditAutoPaymentMethodConditionsSummaryProps) => {
  const { sm } = useScreen();
  const { palette } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();
  const { watch } = useFormContext();

  const financing = watch('financing');
  const possibleLenders = financing?.lenders;
  const perMonthLabel = getTextIdTranslation('PER_MONTH');
  const monthsLabel = getTextIdTranslation('MONTHS');

  const hasInstalmentChanged = initialConditions.instalmentAmount !== newConditions.instalmentAmount;

  const formatInstalment = (amount: string) => `${amount}${perMonthLabel}`;
  const formatTerm = (term: string) => `${term} ${monthsLabel}`;
  const formatInsurance = (value: string) => (value === '-' ? getTextIdTranslation('NO_INSURANCE') : value);
  const financingConditionRows: EditAutoPaymentMethodSummaryRowData[] = [
    {
      labelTextId: 'TERM',
      originalValue: formatTerm(initialConditions.term),
      newValue: formatTerm(newConditions.term),
      hasChanged: initialConditions.term !== newConditions.term,
    },
    {
      labelTextId: 'SETUP_FEE_SHORT',
      originalValue: initialConditions.setupFee,
      newValue: newConditions.setupFee,
      hasChanged: initialConditions.setupFee !== newConditions.setupFee,
    },
    {
      labelTextId: 'TIN',
      originalValue: initialConditions.annualInterest,
      newValue: newConditions.annualInterest,
      hasChanged: initialConditions.annualInterest !== newConditions.annualInterest,
    },
    {
      labelTextId: 'TAE',
      originalValue: initialConditions.apr,
      newValue: newConditions.apr,
      hasChanged: initialConditions.apr !== newConditions.apr,
    },
    {
      labelTextId: 'INSURANCE',
      originalValue: formatInsurance(initialConditions.insurance),
      newValue: formatInsurance(newConditions.insurance),
      hasChanged: initialConditions.insurance !== newConditions.insurance,
    },
  ];

  const amountBreakdownRows: EditAutoPaymentMethodSummaryRowData[] = [
    {
      labelTextId: 'CASH_PRICE',
      originalValue: initialConditions.vehicleAmount,
      newValue: newConditions.vehicleAmount,
      hasChanged: initialConditions.vehicleAmount !== newConditions.vehicleAmount,
    },
    {
      labelTextId: 'DOWN_PAYMENT',
      originalValue: initialConditions.downPayment,
      newValue: newConditions.downPayment,
      hasChanged: initialConditions.downPayment !== newConditions.downPayment,
    },
    {
      labelTextId: 'TOTAL_DISCOUNT',
      originalValue: initialConditions.discount,
      newValue: newConditions.discount,
      hasChanged: initialConditions.discount !== newConditions.discount,
    },
    {
      labelTextId: 'TOTAL_TO_FINANCE',
      originalValue: initialConditions.totalToFinance,
      newValue: newConditions.totalToFinance,
      hasChanged: initialConditions.totalToFinance !== newConditions.totalToFinance,
    },
  ];

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      justifyContent="center"
      gridGap={sm ? 12 : 8}
      width="100%"
    >
      <Font variant="subtitle1" color="title" style={{ fontWeight: 600 }}>
        <Text tId="NEW_FINANCING_CONDITIONS" />
      </Font>
      <CustomPaper
        fill="paper"
        style={{
          minWidth: 0,
          ...(isInvalid && { border: `1px solid ${palette.error.main}` }),
        }}
      >
        <Box
          display="flex"
          flexDirection={sm ? 'row' : 'column'}
          justifyContent={sm ? 'space-between' : 'flex-start'}
          alignItems="flex-start"
          gridGap={sm ? 16 : 12}
          width="100%"
        >
          <Box display="flex" flexDirection="column" alignItems="baseline" flexWrap="wrap" gridGap={0} minWidth={0}>
            <Font variant="h2" color="title" style={{ wordBreak: 'break-word', fontWeight: 700 }}>
              {formatInstalment(newConditions.instalmentAmount)}
            </Font>
            {hasInstalmentChanged && (
              <Font strikethrough variant="h4" style={{ wordBreak: 'break-word' }}>
                ({formatInstalment(initialConditions.instalmentAmount)})
              </Font>
            )}
          </Box>
          {selectedLenderName ? (
            <Box flexShrink={0} mt={sm ? 0.5 : 0}>
              <LenderLogo lenderName={selectedLenderName} size={28} />
            </Box>
          ) : possibleLenders?.length > 0 ? (
            <Box display="flex" flexDirection="row" alignItems="center" gridGap={8}>
              {possibleLenders.map((lender: LenderName, key: number) => (
                <Box flexShrink={0} mt={sm ? 0.5 : 0} key={key}>
                  <LenderIcon lenderName={lender} size={28} />
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>

        {financingConditionRows.length > 0 && (
          <Box mt={3}>
            <EditAutoPaymentMethodSummarySection rows={financingConditionRows} />
            <CustomDivider my={2} />
            <EditAutoPaymentMethodSummarySection rows={amountBreakdownRows} />
          </Box>
        )}
      </CustomPaper>
    </Box>
  );
};
